import { MarkerF, PolylineF } from "@react-google-maps/api";
import React, { useEffect, useRef, useState, memo } from "react";

const TripIconAnimation = ({ 
  tripPath,
  isTripDataLoading,
  vehicleTripView,
}) => {

  // Simplified state - only track what's needed for rendering
  const [animationState, setAnimationState] = useState({
    currentPath: [],
    isComplete: false,
    isAnimating: false
  });
    
  // Use refs for animation control
  const animationRef = useRef(null);
  const pathRef = useRef([]);
  const startTimeRef = useRef(null);
    
  // Animation constants
  const ANIMATION_DURATION = 15000; // 5 seconds
  const PAUSE_DURATION = 5000; // 5 seconds pause before restarting
    
  useEffect(() => {
    /*
      console.log("TripIconAnimation_2.js: useEffect: tripPath:", {
        isTripDataLoading,
        tripPath,
        vehicleTripView,
      });
      */

    // Clear any existing animation
    stopAnimation();
    
    // Reset animation when path changes
    if (!vehicleTripView || isTripDataLoading || !tripPath?.length || tripPath.length < 2) {
      setAnimationState({
        currentPath: [],
        isComplete: false,
        isAnimating: false
      });
      return;
    }
      
    // Sample and store the path for smoother animation
    pathRef.current = getSampledPath(tripPath);
      
    // Reset animation state
    setAnimationState({
      currentPath: [pathRef.current[0]], // Start with first point
      isComplete: false,
      isAnimating: true
    });
      
    startTimeRef.current = null;
      
    // Start animation
    // startAnimation();

    // Delay animation start slightly to ensure clean state
    setTimeout(() => {
      startAnimation();
    }, 100);
      
    // Cleanup on unmount or path change
    return stopAnimation;
  }, [tripPath, isTripDataLoading, vehicleTripView]);
    
  // Sampling function to reduce points for performance
  const getSampledPath = (path, maxPoints = 200) => {
    if (!path || path.length <= maxPoints) return path;
      
    const pathLength = path.length;
    const step = Math.ceil(pathLength / maxPoints);
    const sampled = [];
      
    for (let i = 0; i < pathLength; i += step) {
      sampled.push(path[i]);
    }
      
    // Always include the last point
    if (pathLength > 0 && sampled[sampled.length - 1] !== path[pathLength - 1]) {
      sampled.push(path[pathLength - 1]);
    }
      
    return sampled;
  };
    
  // Animation function using requestAnimationFrame
  const animate = (timestamp) => {
    if (!vehicleTripView) {
      stopAnimation();
      return;
    }

    // Initialize start time on first frame
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }
      
    // Calculate progress (0 to 1)
    const elapsed = timestamp - startTimeRef.current;
    const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      
    // Calculate how many points to show based on progress
    const pathLength = pathRef.current.length;
    const pointIndex = Math.floor(progress * pathLength);
      
    // Update path for rendering
    if (pointIndex > 0 && pointIndex <= pathLength) {
      setAnimationState(() => ({
        currentPath: pathRef.current.slice(0, pointIndex + 1),
        isComplete: progress >= 1,
        isAnimating: true
      }));
    }
      
    // Continue animation if not complete
    if (progress < 1) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      // Schedule reset after pause
      setTimeout(() => {
        if (animationRef.current !== null) { // Only if not unmounted
          setAnimationState({
            currentPath: [pathRef.current[0]],
            isComplete: false,
            isAnimating: true
          });
          startTimeRef.current = null;
          animationRef.current = requestAnimationFrame(animate);
        }
      }, PAUSE_DURATION);
    }
  };
    
  // Start animation helper
  const startAnimation = () => {
    stopAnimation(); // Ensure any existing animation is stopped
    animationRef.current = requestAnimationFrame(animate);
  };
    
  // Stop animation helper
  const stopAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };
    
  // Polyline options
  const polylineOptions = {
    geodesic: true,
    strokeColor: "#ef8157",
    strokeOpacity: 1,
    strokeWeight: 6,
    zIndex: 200,
  };
    
  // Marker icon
  const markerIcon = {
    url: "/assets/img/history-icons/icons8-record-30_new_B.png",
    scaledSize: new window.google.maps.Size(30, 30),
    anchor: new window.google.maps.Point(15, 15),
  };
    
  const { currentPath, isComplete } = animationState;
    
  // Don't render anything if path is empty
  if (!currentPath.length) return null;
    
  return (
    <>
      {!isComplete && currentPath.length > 0 && (
        <MarkerF
          position={currentPath[currentPath.length - 1]}
          icon={markerIcon}
          zIndex={1000}
        />
      )}
      <PolylineF path={currentPath} options={polylineOptions} />
    </>
  );
};

// Use memo to prevent unnecessary re-renders
export default memo(TripIconAnimation);